import React, { useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useProgram } from '../hooks/useProgram';
import { sendTx } from '../utils/transactions';
import {
  derivePoolPda,
  deriveUserStakePda,
  createStakeInstruction,
  createUnstakeInstruction,
  createClaimRewardsInstruction,
} from '../utils/instructions';
import { PublicKey } from '@solana/web3.js';
import { MONOPOLY_MINT, ROLL_MINT } from '../constants';
import { ArrowDownCircle, ChevronDown, ChevronUp, Coins, Gift } from 'lucide-react';
import GradientButton from './GradientButton';
import StakingCard from './StakingCard';
import InputWithMax from './InputWithMax';
import { formatTokenBalance, parseSmallestAmount } from '../utils/format';
import { useModal } from '../hooks/useFeedbackModal';
import { getAssociatedTokenAddress } from '@solana/spl-token';

const StatCard = ({ icon: Icon, title, value, action }) => (
  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
    <div className="flex items-center justify-between">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${title.includes('Staked') ? 'text-purple-500' : 'text-pink-500'}`} />
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        </div>
        <p className="text-2xl font-bold">{value}</p>
      </div>
      {action}
    </div>
  </div>
);

export default function UserStake({ userStakeData, userBalances, poolState, refreshUser, refreshPool }) {
  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [showUnstake, setShowUnstake] = useState(false);
  const { publicKey, signTransaction } = useWallet();
  const { connection, monopolyMint, rollMint } = useProgram();
  const modal = useModal();
  const [isStaking, setIsStaking] = useState(false);
  const [isUnstaking, setIsUnstaking] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);

  const pendingRewards = calculatePendingRewards(userStakeData, poolState);
  const hasStakedBalance = BigInt(userStakeData.staked_amount.toString()) > 0n;

  function calculatePendingRewards(uData, pData) {
    const staked = BigInt(uData.staked_amount.toString());
    const global_rpt = BigInt(pData.global_reward_per_token.toString());
    const debt = BigInt(uData.reward_debt.toString());
    const accrued = staked * global_rpt;
    const pending_scaled = accrued > debt ? accrued - debt : BigInt(0);
    const SCALING_FACTOR = 1_000_000_000_000n;
    const pending = pending_scaled / SCALING_FACTOR;
    return formatTokenBalance(pending, 9, 5);
  }

  const handleMaxStake = () => {
    const formatted = formatTokenBalance(userBalances.monopolyBalance, 6, 1);
    setStakeAmount(formatted);
  };

  const handleMaxUnstake = () => {
    const formatted = formatTokenBalance(BigInt(userStakeData.staked_amount.toString()), 6, 1);
    setUnstakeAmount(formatted);
  };

  async function handleStake() {
    if (!publicKey || !signTransaction) return;

    const smallestAmount = parseSmallestAmount(stakeAmount, 6);
    if (smallestAmount <= 0n) {
      modal.showModal({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount to stake.',
        type: 'error'
      });
      return;
    }

    setIsStaking(true);
    try {
      const [poolPda] = derivePoolPda(monopolyMint, rollMint);
      const [userStakePda] = await deriveUserStakePda(poolPda, publicKey);
      const user_monopoly_account = await getAssociatedTokenAddress(
        new PublicKey(MONOPOLY_MINT),
        publicKey
      );
      const monopoly_stake_vault = new PublicKey(poolState.monopoly_stake_vault);
      const monopoly_fee_vault = new PublicKey(poolState.monopoly_fee_vault);
      const user_roll_account = await getAssociatedTokenAddress(
        new PublicKey(ROLL_MINT),
        publicKey
      );

      const ix = createStakeInstruction(
        publicKey,
        poolPda,
        userStakePda,
        user_monopoly_account,
        monopoly_stake_vault,
        monopoly_fee_vault,
        Number(smallestAmount),
        rollMint,
        user_roll_account
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      modal.showModal({
        title: 'Success',
        description: `Successfully staked ${stakeAmount} MONOPOLY`,
        type: 'success'
      });
      setStakeAmount('');
      setTimeout(() => {
      refreshUser();
      refreshPool();
      }, 3000)
    } catch (err) {
      console.error(err);
      modal.showModal({
        title: 'Staking Failed',
        description: err.message,
        type: 'error'
      });
    } finally {
      setIsStaking(false);
    }
  }

  async function handleUnstake() {
    if (!publicKey || !signTransaction) return;

    const smallestAmount = parseSmallestAmount(unstakeAmount, 6);
    if (smallestAmount <= 0n || smallestAmount > BigInt(userStakeData.staked_amount.toString())) {
      modal.showModal({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount to unstake.',
        type: 'error'
      });
      return;
    }

    setIsUnstaking(true);
    try {
      const [poolPda] = derivePoolPda(monopolyMint, rollMint);
      const [userStakePda] = await deriveUserStakePda(poolPda, publicKey);
      const user_roll_account = await getAssociatedTokenAddress(
        new PublicKey(ROLL_MINT),
        publicKey
      );
      const user_monopoly_account = await getAssociatedTokenAddress(
        new PublicKey(MONOPOLY_MINT),
        publicKey
      );
      const monopoly_stake_vault = new PublicKey(poolState.monopoly_stake_vault);

      const ix = createUnstakeInstruction(
        publicKey,
        poolPda,
        userStakePda,
        user_roll_account,
        user_monopoly_account,
        monopoly_stake_vault,
        rollMint,
        Number(smallestAmount)
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      modal.showModal({
        title: 'Success',
        description: `Successfully unstaked ${unstakeAmount} MONOPOLY`,
        type: 'success'
      });
      setUnstakeAmount('');
      setShowUnstake(false);
      setTimeout(() => {
      refreshUser();
      refreshPool();
      }, 3000)
    } catch (err) {
      console.error(err);
      modal.showModal({
        title: 'Unstaking Failed',
        description: err.message,
        type: 'error'
      });
    } finally {
      setIsUnstaking(false);
    }
  }

  async function handleClaim() {
    if (!publicKey || !signTransaction) return;

    setIsClaiming(true);
    try {
      const [poolPda] = derivePoolPda(monopolyMint, rollMint);
      const [userStakePda] = await deriveUserStakePda(poolPda, publicKey);
      const user_roll_account = await getAssociatedTokenAddress(
        new PublicKey(ROLL_MINT),
        publicKey
      );

      const ix = createClaimRewardsInstruction(
        publicKey,
        poolPda,
        userStakePda,
        user_roll_account,
        rollMint
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      modal.showModal({
        title: 'Success',
        description: 'Successfully claimed rewards',
        type: 'success'
      });
      setTimeout(() => {
      refreshUser();
      refreshPool();
      }, 3000)
    } catch (err) {
      console.error(err);
      modal.showModal({
        title: 'Claim Failed',
        description: err.message,
        type: 'error'
      });
    } finally {
      setIsClaiming(false);
    }
  }

  return (
    <StakingCard>
      <div className="space-y-8">
        {/* Main Staking Section */}
        <div className="space-y-4">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600">
              Stake MONOPOLY
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Stake your MONOPOLY tokens to earn ROLL rewards
            </p>
          </div>

          <div className="p-6 bg-purple-50 dark:bg-purple-900/10 rounded-xl space-y-4">
            <InputWithMax
              value={stakeAmount}
              onChange={(e) => setStakeAmount(e.target.value)}
              placeholder="Enter amount to stake"
              onMaxClick={handleMaxStake}
            />
            <GradientButton
              onClick={handleStake}
              disabled={isStaking}
              className="w-full py-4 text-lg group"
            >
              <div className="flex items-center justify-center gap-2">
                {isStaking ? (
                  <>Loading...</>
                ) : (
                  <>
                    Stake MONOPOLY
                    <Coins className="w-5 h-5 transition-transform group-hover:scale-110" />
                  </>
                )}
              </div>
            </GradientButton>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            icon={Coins}
            title="Staked MONOPOLY"
            value={formatTokenBalance(BigInt(userStakeData.staked_amount.toString()), 6, 1)}
          />
          <StatCard
            icon={Gift}
            title="Pending ROLL"
            value={pendingRewards}
            action={
              <GradientButton
                onClick={handleClaim}
                disabled={isClaiming || parseFloat(pendingRewards) === 0}
                className="!px-3 !py-1.5 text-sm"
              >
                {isClaiming ? 'Claiming...' : 'Claim'}
              </GradientButton>
            }
          />
        </div>

        {/* Unstake Section (Only shown if user has staked balance) */}
        {hasStakedBalance && (
          <div className="border-t pt-6">
            <button
              onClick={() => setShowUnstake(!showUnstake)}
              className="w-full flex items-center justify-between p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <span className="flex items-center gap-2">
                <ArrowDownCircle className="w-5 h-5" />
                Unstake MONOPOLY
              </span>
              {showUnstake ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>

            {showUnstake && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl space-y-4">
                <InputWithMax
                  value={unstakeAmount}
                  onChange={(e) => setUnstakeAmount(e.target.value)}
                  placeholder="Amount to unstake"
                  onMaxClick={handleMaxUnstake}
                />
                <GradientButton
                  onClick={handleUnstake}
                  disabled={isUnstaking}
                  className="w-full"
                >
                  {isUnstaking ? 'Unstaking...' : 'Unstake'}
                </GradientButton>
              </div>
            )}
          </div>
        )}
      </div>
    </StakingCard>
  );
}
